import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    Loader2, CalendarDays, CalendarClock, Radio, CheckCircle2,
    ClipboardCheck, BookOpen, FolderOpen, Settings, ArrowRight,
    Users, RefreshCcw, X, ExternalLink, Sparkles, Search, Pin, PinOff,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatReceivedAt } from "../../lib/dateFormat";

/**
 * V170 — Classroom Hub
 *
 * Bento-style tile grid landing view for the Admin Classroom & Assignments
 * module. Each tile is a jump-off point into a filtered drill-down list.
 *
 * Wired to:
 *   GET /admin/classroom/hub-stats
 *   GET /admin/classroom/sessions?window=today|upcoming|live|completed|attendance_pending
 *   GET /admin/classroom/homework-pending
 *   GET /admin/classroom/materials
 */

const REFRESH_MS = 60_000;

function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    } catch {
        return iso;
    }
}

function fmtTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleTimeString("en-IN", {
            timeZone: "Asia/Kolkata",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    } catch {
        return "";
    }
}

// ---------------------------------------------------------------- Drill panel

function DrillPanel({ tile, onClose, onOpenAssignment }) {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        let url = "";
        if (tile.kind === "sessions") {
            url = `/admin/classroom/sessions?window=${tile.window}&limit=100`;
        } else if (tile.kind === "homework") {
            url = "/admin/classroom/homework-pending?limit=100";
        } else if (tile.kind === "materials") {
            url = "/admin/classroom/materials?limit=100";
        }
        api.get(url)
            .then((r) => {
                if (cancelled) return;
                setItems(r.data?.items || []);
            })
            .catch(() => !cancelled && toast.error("Failed to load"))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [tile]);

    return (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50" data-testid="hub-drill-overlay" onClick={onClose}>
            <div
                className="w-full sm:max-w-3xl max-h-[92vh] bg-white rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-2xl classroom-mobile"
                onClick={(e) => e.stopPropagation()}
                data-testid="hub-drill-panel"
            >
                <div className={`${tile.grad} px-4 py-3.5 flex items-center gap-3 text-white`}>
                    <tile.icon className="w-5 h-5" />
                    <div className="flex-1 min-w-0">
                        <div className="font-heading font-bold text-base leading-tight truncate">{tile.title}</div>
                        <div className="text-[11px] opacity-90 truncate">{tile.subtitle}</div>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="hub-drill-close"
                        data-cm-compact
                        aria-label="Close"
                        className="rounded-full p-1.5 bg-white/20 hover:bg-white/30"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="overflow-y-auto max-h-[calc(92vh-64px)] p-3 sm:p-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-slate-400">
                            <Loader2 className="w-5 h-5 animate-spin" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-14">
                            <div className="text-sm font-semibold text-slate-700">Nothing here right now</div>
                            <div className="text-xs text-slate-500 mt-1">{tile.emptyHint}</div>
                        </div>
                    ) : tile.kind === "sessions" ? (
                        <ul className="space-y-2">
                            {items.map((s) => (
                                <li key={s.id} className="border border-slate-200 rounded-xl p-3 bg-white">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-heading font-bold text-[#0F172A] text-sm truncate">
                                                {s.assignment_code || "—"} · Session #{s.session_number}
                                                {s.is_demo && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200">DEMO</span>}
                                            </div>
                                            <div className="text-xs text-slate-700 mt-0.5 truncate">{s.student_name || "—"} · {s.subject || "—"}{s.grade ? ` · ${s.grade}` : ""}</div>
                                            <div className="text-[11px] text-slate-500 mt-0.5 truncate">Tutor: {s.tutor_name || "—"}</div>
                                            <div className="text-[11px] text-slate-500 mt-0.5">{fmtDateTime(s.scheduled_start)} → {fmtTime(s.scheduled_end)}</div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200 capitalize">{s.status}</span>
                                            <button
                                                onClick={() => onOpenAssignment && onOpenAssignment(s.assignment_id)}
                                                data-cm-compact
                                                data-testid={`hub-session-open-${s.id}`}
                                                className="text-[11px] font-semibold text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-0.5"
                                            >
                                                Open <ArrowRight className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                    {s.meeting_url && (
                                        <a
                                            href={s.meeting_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:text-blue-900"
                                        >
                                            <ExternalLink className="w-3 h-3" /> Meeting link
                                        </a>
                                    )}
                                </li>
                            ))}
                        </ul>
                    ) : tile.kind === "homework" ? (
                        <ul className="space-y-2">
                            {items.map((s) => (
                                <li key={s.id} className="border border-slate-200 rounded-xl p-3 bg-white">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-heading font-bold text-[#0F172A] text-sm truncate">{s.homework_title || "Homework"}</div>
                                            <div className="text-xs text-slate-700 mt-0.5 truncate">{s.student_name || "—"} · {s.subject || "—"}</div>
                                            <div className="text-[11px] text-slate-500 mt-0.5 truncate">Tutor: {s.tutor_name || "—"} · {s.assignment_code || "—"}</div>
                                            <div className="text-[11px] text-slate-500 mt-0.5">Submitted: {fmtDateTime(s.submitted_at)}</div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">Pending review</span>
                                            <button
                                                onClick={() => onOpenAssignment && onOpenAssignment(s.assignment_id)}
                                                data-cm-compact
                                                data-testid={`hub-hw-open-${s.id}`}
                                                className="text-[11px] font-semibold text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-0.5"
                                            >
                                                Open <ArrowRight className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <ul className="space-y-2">
                            {items.map((m) => (
                                <li key={m.id} className="border border-slate-200 rounded-xl p-3 bg-white">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-heading font-bold text-[#0F172A] text-sm truncate">{m.title || m.original_filename || "Material"}</div>
                                            <div className="text-xs text-slate-700 mt-0.5 truncate">{m.student_name || "—"} · {m.subject || "—"}</div>
                                            <div className="text-[11px] text-slate-500 mt-0.5 truncate">Tutor: {m.tutor_name || "—"} · {m.assignment_code || "—"}</div>
                                            <div className="text-[11px] text-slate-500 mt-0.5">Uploaded: {fmtDateTime(m.created_at)}</div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200 capitalize">{m.category || "material"}</span>
                                            <button
                                                onClick={() => onOpenAssignment && onOpenAssignment(m.assignment_id)}
                                                data-cm-compact
                                                data-testid={`hub-material-open-${m.id}`}
                                                className="text-[11px] font-semibold text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-0.5"
                                            >
                                                Open <ArrowRight className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------- Hub search

const RECENT_KEY = "at_hub_recent_jumps";
const RECENT_MAX = 5;
const PINNED_KEY = "at_hub_pinned";
const PINNED_MAX = 10;

function loadRecent() {
    try {
        const raw = localStorage.getItem(RECENT_KEY);
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.slice(0, RECENT_MAX) : [];
    } catch { return []; }
}

function saveRecent(list) {
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX))); } catch { /* ignore quota */ }
}

function loadPinned() {
    try {
        const raw = localStorage.getItem(PINNED_KEY);
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.slice(0, PINNED_MAX) : [];
    } catch { return []; }
}

function savePinned(list) {
    try { localStorage.setItem(PINNED_KEY, JSON.stringify(list.slice(0, PINNED_MAX))); } catch { /* ignore quota */ }
}

function toEntry(a) {
    return {
        id: a.id,
        student_name: a.student_name || "—",
        tutor_name: a.tutor_name || "",
        subject: a.subject || "",
        grade: a.grade || "",
        assignment_code: a.assignment_code || "",
        is_demo: !!a.is_demo,
    };
}

function HubSearch({ onOpenAssignment }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [activeIdx, setActiveIdx] = useState(-1);
    const [recent, setRecent] = useState(loadRecent);
    const [pinned, setPinned] = useState(loadPinned);
    const wrapRef = useRef(null);

    const isPinned = (id) => pinned.some((p) => p.id === id);

    // Debounced fetch — leverages the existing bucket=all search on the
    // assignments API (matches student, parent, tutor, subject, code, BMT id).
    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) { setResults([]); return; }
        setLoading(true);
        const timer = setTimeout(() => {
            api.get(`/admin/assignments?bucket=all&search=${encodeURIComponent(q)}`)
                .then((r) => setResults((r.data?.items || []).slice(0, 8)))
                .catch(() => setResults([]))
                .finally(() => setLoading(false));
        }, 250);
        return () => clearTimeout(timer);
    }, [query]);

    // Close on outside click
    useEffect(() => {
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const pushRecent = (a) => {
        // Pinned students live in their own row above Recent — don't duplicate.
        if (isPinned(a.id)) return;
        const entry = { ...toEntry(a), opened_at: new Date().toISOString() };
        setRecent((prev) => {
            const next = [entry, ...prev.filter((p) => p.id !== a.id)].slice(0, RECENT_MAX);
            saveRecent(next);
            return next;
        });
    };

    const clearRecent = () => { setRecent([]); saveRecent([]); };

    const togglePin = (a) => {
        setPinned((prev) => {
            if (prev.some((p) => p.id === a.id)) {
                const next = prev.filter((p) => p.id !== a.id);
                savePinned(next);
                toast.success("Unpinned");
                return next;
            }
            if (prev.length >= PINNED_MAX) {
                toast.error(`You can pin up to ${PINNED_MAX} students`);
                return prev;
            }
            const next = [toEntry(a), ...prev];
            savePinned(next);
            // If this student is also in Recent, remove — pinned row will show it.
            setRecent((r) => {
                const cleaned = r.filter((x) => x.id !== a.id);
                if (cleaned.length !== r.length) saveRecent(cleaned);
                return cleaned;
            });
            toast.success("Pinned to Hub");
            return next;
        });
    };

    const pick = (a) => {
        pushRecent(a);
        setOpen(false);
        setQuery("");
        setResults([]);
        setActiveIdx(-1);
        onOpenAssignment && onOpenAssignment(a.id);
    };

    const onKeyDown = (e) => {
        if (!open) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, results.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
        else if (e.key === "Enter" && activeIdx >= 0 && results[activeIdx]) { e.preventDefault(); pick(results[activeIdx]); }
        else if (e.key === "Escape") { setOpen(false); setActiveIdx(-1); }
    };

    return (
        <div ref={wrapRef} className="relative" data-testid="hub-search-wrap">
            <div className="relative">
                <Search className="absolute top-2.5 left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIdx(-1); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    placeholder="Jump to any student, tutor, subject or code…"
                    data-testid="hub-search-input"
                    className="w-full pl-9 pr-9 py-2.5 text-sm bg-white border border-slate-200 rounded-xl focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                />
                {query && (
                    <button
                        type="button"
                        onClick={() => { setQuery(""); setResults([]); setActiveIdx(-1); }}
                        data-testid="hub-search-clear"
                        aria-label="Clear"
                        className="absolute top-2.5 right-2.5 rounded-full p-0.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            {/* Pinned favourites — always shown above Recent when the query is
                empty. Pinned students never fall off; unpin via the × icon. */}
            {query.trim().length === 0 && pinned.length > 0 && (
                <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="hub-pinned-chips">
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold text-amber-600 mr-1">
                        <Pin className="w-3 h-3" /> Pinned
                    </span>
                    {pinned.map((p) => (
                        <span
                            key={p.id}
                            data-testid={`hub-pinned-chip-${p.id}`}
                            title={`${p.student_name}${p.tutor_name ? " · Tutor: " + p.tutor_name : ""}${p.assignment_code ? " · " + p.assignment_code : ""}`}
                            className="inline-flex items-center gap-1 pl-1 pr-1 py-1 bg-amber-50 border border-amber-200 rounded-full text-xs font-semibold text-amber-900 hover:bg-amber-100 transition-colors"
                        >
                            <button
                                type="button"
                                onClick={() => pick(p)}
                                data-testid={`hub-pinned-open-${p.id}`}
                                className="inline-flex items-center gap-1.5 pr-1.5"
                            >
                                <span className="w-5 h-5 rounded-full bg-amber-200/70 text-amber-900 flex items-center justify-center text-[9px] font-heading font-bold">
                                    {(p.student_name || "?").slice(0, 2).toUpperCase()}
                                </span>
                                <span className="max-w-[7rem] sm:max-w-[10rem] truncate">{p.student_name}</span>
                                {p.is_demo && <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200">DEMO</span>}
                            </button>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); togglePin(p); }}
                                data-testid={`hub-pinned-unpin-${p.id}`}
                                title="Unpin"
                                aria-label="Unpin"
                                className="rounded-full p-0.5 text-amber-700 hover:text-amber-900 hover:bg-amber-200/70"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {/* Recent jumps — shown whenever the query is empty and admin has opened
                a student before. Chips stay visible (they don't rely on focus) so
                one tap re-opens the last N students without any typing. */}
            {query.trim().length === 0 && recent.length > 0 && (
                <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="hub-recent-chips">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 mr-1">Recent</span>
                    {recent.map((r) => (
                        <span
                            key={r.id}
                            data-testid={`hub-recent-chip-${r.id}`}
                            title={`${r.student_name}${r.tutor_name ? " · Tutor: " + r.tutor_name : ""}${r.assignment_code ? " · " + r.assignment_code : ""}`}
                            className="inline-flex items-center gap-1 pl-1 pr-1 py-1 bg-white border border-slate-200 rounded-full text-xs font-semibold text-slate-700 hover:border-cyan-300 hover:text-cyan-800 hover:bg-cyan-50/50 transition-colors group"
                        >
                            <button
                                type="button"
                                onClick={() => pick(r)}
                                data-testid={`hub-recent-open-${r.id}`}
                                className="inline-flex items-center gap-1.5 pr-1.5"
                            >
                                <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-[9px] font-heading font-bold">
                                    {(r.student_name || "?").slice(0, 2).toUpperCase()}
                                </span>
                                <span className="max-w-[7rem] sm:max-w-[10rem] truncate">{r.student_name}</span>
                                {r.is_demo && <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200">DEMO</span>}
                            </button>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); togglePin(r); }}
                                data-testid={`hub-recent-pin-${r.id}`}
                                title="Pin to Hub"
                                aria-label="Pin"
                                className="rounded-full p-0.5 text-slate-300 hover:text-amber-600 hover:bg-amber-50 opacity-70 group-hover:opacity-100 transition-opacity"
                            >
                                <Pin className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                    <button
                        type="button"
                        onClick={clearRecent}
                        data-testid="hub-recent-clear"
                        title="Clear recent"
                        className="text-[10px] font-semibold text-slate-400 hover:text-slate-700 ml-0.5 inline-flex items-center gap-0.5"
                    >
                        <X className="w-3 h-3" /> Clear
                    </button>
                </div>
            )}

            {open && query.trim().length >= 2 && (
                <div
                    className="absolute z-50 mt-1.5 left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-xl max-h-96 overflow-y-auto"
                    data-testid="hub-search-results"
                >
                    {loading ? (
                        <div className="flex items-center justify-center py-6 text-slate-400 text-xs gap-2">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
                        </div>
                    ) : results.length === 0 ? (
                        <div className="py-6 text-center text-xs text-slate-500">
                            No matches for <span className="font-semibold text-slate-700">"{query.trim()}"</span>
                        </div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {results.map((a, idx) => {
                                const pinnedNow = isPinned(a.id);
                                return (
                                <li key={a.id} className={`flex items-stretch ${activeIdx === idx ? "bg-cyan-50" : "hover:bg-slate-50"}`}>
                                    <button
                                        type="button"
                                        onClick={() => pick(a)}
                                        onMouseEnter={() => setActiveIdx(idx)}
                                        data-testid={`hub-search-result-${a.id}`}
                                        className="flex-1 text-left px-3 py-2.5 flex items-center gap-3"
                                    >
                                        <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0 text-xs font-heading font-bold">
                                            {(a.student_name || "?").slice(0, 2).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-heading font-bold text-[#0F172A] text-sm truncate">{a.student_name || "—"}</span>
                                                {a.is_demo && <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200">DEMO</span>}
                                                {pinnedNow && <Pin className="w-3 h-3 text-amber-600 fill-amber-600" />}
                                            </div>
                                            <div className="text-[11px] text-slate-500 truncate">
                                                Tutor: <span className="text-slate-700">{a.tutor_name || "—"}</span>
                                                {" · "}{a.subject || "—"}{a.grade ? ` · ${a.grade}` : ""}
                                            </div>
                                        </div>
                                        <div className="shrink-0 text-right">
                                            <div className="text-[10px] font-mono font-semibold text-slate-600">{a.assignment_code}</div>
                                            <div className="text-[9px] text-slate-400 capitalize truncate">{(a.status || "").replaceAll("_", " ")}</div>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); togglePin(a); }}
                                        onMouseEnter={() => setActiveIdx(idx)}
                                        data-testid={`hub-search-pin-${a.id}`}
                                        title={pinnedNow ? "Unpin" : "Pin to Hub"}
                                        aria-label={pinnedNow ? "Unpin" : "Pin"}
                                        aria-pressed={pinnedNow}
                                        className={`shrink-0 px-3 flex items-center justify-center border-l border-slate-100 transition-colors ${
                                            pinnedNow
                                                ? "text-amber-600 bg-amber-50 hover:bg-amber-100"
                                                : "text-slate-300 hover:text-amber-600 hover:bg-amber-50"
                                        }`}
                                    >
                                        {pinnedNow ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                                    </button>
                                </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------- Hub tile

function Tile({ testid, icon: Icon, title, count, hint, span, grad, pulse, onClick, disabled, link, hero }) {
    const inner = (
        <>
            <div className="flex items-start justify-between gap-2 relative z-10">
                <div className={`inline-flex items-center justify-center rounded-xl bg-white/25 backdrop-blur-sm ${hero ? "w-9 h-9 sm:w-11 sm:h-11" : "w-9 h-9"}`}>
                    <Icon className={`${hero ? "w-4 h-4 sm:w-5 sm:h-5" : "w-4 h-4"} text-white`} />
                </div>
                {pulse && (
                    <span className="relative flex h-2.5 w-2.5" aria-hidden>
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
                    </span>
                )}
            </div>
            <div className="mt-auto relative z-10">
                <div className={`font-heading font-black tracking-tight text-white leading-none ${hero ? "text-3xl sm:text-6xl" : "text-3xl"}`}>
                    {typeof count === "number" ? count.toLocaleString("en-IN") : count}
                </div>
                <div className={`mt-1.5 font-heading font-bold text-white ${hero ? "text-sm sm:text-lg" : "text-sm"}`}>{title}</div>
                {hint && <div className="text-[11px] text-white/85 mt-0.5 leading-snug">{hint}</div>}
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-white/70 absolute bottom-2.5 right-2.5" />
            <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10 blur-xl pointer-events-none" aria-hidden />
        </>
    );

    const cls = `relative overflow-hidden rounded-2xl p-3.5 sm:p-4 min-h-[128px] flex flex-col text-left transition-all ${grad} ${disabled ? "opacity-60 cursor-not-allowed" : "hover:brightness-110 active:scale-[0.985] shadow-md hover:shadow-lg"} ${span || ""}`;

    if (link && !disabled) {
        return (
            <Link to={link} data-testid={testid} data-cm-primary className={cls}>
                {inner}
            </Link>
        );
    }
    return (
        <button type="button" onClick={onClick} disabled={disabled} data-testid={testid} data-cm-primary className={cls}>
            {inner}
        </button>
    );
}

// ---------------------------------------------------------------- Main

export default function ClassroomHub({ onNavigate, onOpenAssignment }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [drill, setDrill] = useState(null);

    const load = (silent = false) => {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        api.get("/admin/classroom/hub-stats")
            .then((r) => setStats(r.data))
            .catch(() => toast.error("Failed to load classroom hub"))
            .finally(() => { setLoading(false); setRefreshing(false); });
    };

    useEffect(() => {
        load(false);
        const t = setInterval(() => load(true), REFRESH_MS);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const tiles = useMemo(() => {
        const s = stats || {};
        return [
            {
                key: "live",
                testid: "hub-tile-live",
                icon: Radio,
                title: "Live now",
                count: s.live ?? 0,
                hint: (s.live ?? 0) > 0 ? "Sessions in progress" : "No class live right now",
                span: "sm:col-span-2 sm:row-span-2",
                grad: "bg-gradient-to-br from-rose-500 via-rose-600 to-fuchsia-700",
                pulse: (s.live ?? 0) > 0,
                hero: true,
                kind: "sessions", window: "live",
                subtitle: "Sessions currently in the live window",
                emptyHint: "No classes are running right now.",
            },
            {
                key: "today",
                testid: "hub-tile-today",
                icon: CalendarDays,
                title: "Today",
                count: s.today ?? 0,
                hint: "Sessions scheduled today",
                grad: "bg-gradient-to-br from-cyan-500 to-blue-600",
                kind: "sessions", window: "today",
                subtitle: "All sessions scheduled for today (IST)",
                emptyHint: "No classes scheduled for today.",
            },
            {
                key: "upcoming",
                testid: "hub-tile-upcoming",
                icon: CalendarClock,
                title: "Upcoming",
                count: s.upcoming ?? 0,
                hint: "After today",
                grad: "bg-gradient-to-br from-indigo-500 to-violet-700",
                kind: "sessions", window: "upcoming",
                subtitle: "Confirmed & proposed sessions after today",
                emptyHint: "No upcoming sessions scheduled yet.",
            },
            {
                key: "attendance",
                testid: "hub-tile-attendance",
                icon: ClipboardCheck,
                title: "Attendance",
                count: s.attendance_pending ?? 0,
                hint: "Awaiting marking",
                grad: "bg-gradient-to-br from-amber-500 to-orange-600",
                kind: "sessions", window: "attendance_pending",
                subtitle: "Past sessions with no attendance recorded",
                emptyHint: "All caught up on attendance.",
            },
            {
                key: "homework",
                testid: "hub-tile-homework",
                icon: BookOpen,
                title: "Homework",
                count: s.homework_pending ?? 0,
                hint: "Pending review",
                grad: "bg-gradient-to-br from-emerald-500 to-teal-600",
                kind: "homework",
                subtitle: "Submissions waiting on tutor evaluation",
                emptyHint: "No homework submissions waiting for review.",
            },
            {
                key: "completed",
                testid: "hub-tile-completed",
                icon: CheckCircle2,
                title: "Completed",
                count: s.completed ?? 0,
                hint: "Last 30 days",
                grad: "bg-gradient-to-br from-slate-600 to-slate-800",
                kind: "sessions", window: "completed",
                subtitle: "Sessions completed in the last 30 days",
                emptyHint: "No completed sessions yet.",
            },
            {
                key: "materials",
                testid: "hub-tile-materials",
                icon: FolderOpen,
                title: "Materials",
                count: s.materials ?? 0,
                hint: "Study library",
                grad: "bg-gradient-to-br from-fuchsia-500 to-pink-600",
                kind: "materials",
                subtitle: "Notes, worksheets & reference files",
                emptyHint: "No materials uploaded yet.",
            },
            {
                key: "settings",
                testid: "hub-tile-settings",
                icon: Settings,
                title: "Settings",
                count: <span className="text-2xl">⚙︎</span>,
                hint: "Live classroom config",
                grad: "bg-gradient-to-br from-slate-500 to-slate-700",
                link: "/admin/live-classroom-setup",
            },
        ];
    }, [stats]);

    const openTile = (t) => {
        if (t.link) return; // Link renders as anchor already
        if (!t.kind) return;
        setDrill(t);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20 text-slate-400" data-testid="hub-loading">
                <Loader2 className="w-6 h-6 animate-spin" />
            </div>
        );
    }

    const s = stats || {};

    return (
        <div className="space-y-4 classroom-mobile" data-testid="classroom-hub">
            {/* Sub-header inside hub */}
            <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                    <div className="text-xs sm:text-sm font-semibold text-slate-600 flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-fuchsia-500" />
                        Classroom Hub
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                        {s.active_assignments ?? 0} active assignments · updated {formatReceivedAt(s.generated_at)}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={() => load(true)}
                        disabled={refreshing}
                        data-testid="hub-refresh-btn"
                        data-cm-compact
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:border-slate-300"
                    >
                        <RefreshCcw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                    {onNavigate && (
                        <button
                            type="button"
                            onClick={() => onNavigate("list")}
                            data-testid="hub-view-list-btn"
                            data-cm-compact
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 hover:bg-cyan-100"
                        >
                            <Users className="w-3.5 h-3.5" />
                            All assignments
                        </button>
                    )}
                </div>
            </div>

            {/* Search */}
            <HubSearch onOpenAssignment={onOpenAssignment} />

            {/* Bento grid: 2 cols on mobile, 4 on desktop. Live tile is 2x2 hero. */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 auto-rows-[128px]" data-testid="hub-tile-grid">
                {tiles.map((t) => (
                    <Tile
                        key={t.key}
                        testid={t.testid}
                        icon={t.icon}
                        title={t.title}
                        count={t.count}
                        hint={t.hint}
                        span={t.span}
                        grad={t.grad}
                        pulse={t.pulse}
                        hero={t.hero}
                        link={t.link}
                        onClick={() => openTile(t)}
                    />
                ))}
            </div>

            {drill && (
                <DrillPanel
                    tile={drill}
                    onClose={() => setDrill(null)}
                    onOpenAssignment={(aid) => {
                        setDrill(null);
                        onOpenAssignment && onOpenAssignment(aid);
                    }}
                />
            )}
        </div>
    );
}
